// src/components/generics/PaymentModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";

import Button from "../ui/Button";
import Card from "../ui/Card";
import Badge from "../ui/Badge";
import Icon from "../ui/Icon";

import axiosInstance from "../../utils/AxiosInstance";
import { useTheme } from "../../contexts/ThemeContext";

const DEFAULT_PLANS = [
  // One-time credit packs
  { plan: "SINGLE",     kind: "ONE_TIME",     value: 100,   priceLabel: "$1",         credits: 1,   title: "Single",   tagline: "1 credit · 5 games" },
  { plan: "BASIC",      kind: "ONE_TIME",     value: 500,   priceLabel: "$5",         credits: 10,  title: "Basic",    tagline: "10 credits · 50 games" },
  { plan: "GOLD",       kind: "ONE_TIME",     value: 2000,  priceLabel: "$20",        credits: 50,  title: "Gold",     tagline: "50 credits · 250 games" },
  { plan: "PLATINUM",   kind: "ONE_TIME",     value: 5000,  priceLabel: "$50",        credits: 200, title: "Platinum", tagline: "200 credits · 1,000 games" },

  // Monthly subscriptions — include Scratch-Off Intelligence at varying tiers
  { plan: "PLUS",       kind: "SUBSCRIPTION", value: 599,   priceLabel: "$5.99/month",  credits: 30,  title: "Plus",     tagline: "30 credits · Scratch — one state" },
  { plan: "PRO",        kind: "SUBSCRIPTION", value: 1099,  priceLabel: "$10.99/month", credits: 60,  title: "Pro",      tagline: "60 credits · Scratch — multi-state + alerts" },
  { plan: "ELITE",      kind: "SUBSCRIPTION", value: 1999,  priceLabel: "$19.99/month", credits: 200, title: "Elite",    tagline: "200 credits · Full analytics + Risk & Value Reports" },
];

/**
 * Compact dark-themed payment modal. Same endpoints, same business flows as
 * the previous implementation. Supports Stripe (one-time + subscription) and
 * PayPal (one-time + subscription).
 *
 * Props:
 *   - isOpen, onClose
 *   - loadNewCredits(newCredit): called on successful payment
 *   - initialCreditPlan: plan key like "GOLD" — pre-selects that plan
 *   - initialStep: 1 (plan picker) | 2 (checkout) — defaults 1
 */
export default function PaymentModal({
  isOpen,
  onClose,
  loadNewCredits,
  initialCreditPlan = null,
  initialStep = 1,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const { theme } = useTheme();
  const paypalSubRef = useRef({ subscriptionId: null, providerPlanId: null });

  const [step, setStep] = useState(initialStep);
  const [planKey, setPlanKey] = useState(null);
  const [method, setMethod] = useState("card");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // PayPal one-time order state
  const [paypalTx, setPaypalTx] = useState({ orderId: null, transactionId: null });
  const [paypalInitRequested, setPaypalInitRequested] = useState(false);

  const plans = useMemo(() => DEFAULT_PLANS, []);
  const selectedPlan = useMemo(
    () => plans.find((p) => p.plan === planKey) || null,
    [plans, planKey]
  );
  const isSubscription = selectedPlan?.kind === "SUBSCRIPTION";
  const formattedAmount = selectedPlan ? `$${(selectedPlan.value / 100).toFixed(2)}` : "";

  const PAYPAL_CLIENT_ID = (process.env.REACT_APP_PAYPAL_CLIENT_ID || "").trim();
  const PAYPAL_CURRENCY = (process.env.REACT_APP_PAYPAL_CURRENCY || "USD").trim() || "USD";
  const isPayPalConfigured = Boolean(PAYPAL_CLIENT_ID);

  /* ============================================================
     Lifecycle — reset on open / preselect
     ============================================================ */
  useEffect(() => {
    if (!isOpen) return;
    setStep(Number(initialStep) === 2 ? 2 : 1);
    setMethod("card");
    setMessage("");
    setLoading(false);
    setSuccess(false);
    setPaypalTx({ orderId: null, transactionId: null });
    setPaypalInitRequested(false);
    paypalSubRef.current = { subscriptionId: null, providerPlanId: null };

    if (initialCreditPlan) {
      const normalized = String(initialCreditPlan).toUpperCase();
      if (plans.some((p) => p.plan === normalized)) {
        setPlanKey(normalized);
      } else {
        setPlanKey(null);
      }
    } else {
      setPlanKey(null);
    }
  }, [isOpen, initialCreditPlan, initialStep, plans]);

  // Auto-close after success
  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => {
      if (typeof onClose === "function") onClose();
    }, 2400);
    return () => clearTimeout(t);
  }, [success, onClose]);

  // Lazy-init PayPal one-time order
  useEffect(() => {
    const start = async () => {
      try {
        setMessage("");
        if (!planKey) return;
        if (!isPayPalConfigured) {
          setMessage("PayPal is not configured for this environment.");
          return;
        }
        setLoading(true);
        const res = await axiosInstance.post("/site/payment/paypal", {
          paymentMethod: "PAYPAL",
          creditPlan: planKey,
        });
        const orderId = res?.data?.orderId;
        const transactionId = res?.data?.transactionId;
        if (!orderId || !transactionId) {
          setMessage("Could not start PayPal payment. Try again.");
          return;
        }
        setPaypalTx({ orderId, transactionId });
      } catch (err) {
        const status = err?.response?.status;
        setMessage(
          status === 401 || status === 403
            ? "Your session expired. Please log in again."
            : "Failed to initialize PayPal payment."
        );
      } finally {
        setLoading(false);
      }
    };

    if (
      isOpen &&
      step === 2 &&
      method === "paypal" &&
      planKey &&
      !isSubscription &&
      !paypalInitRequested &&
      !paypalTx.orderId
    ) {
      setPaypalInitRequested(true);
      start();
    }
  }, [
    isOpen,
    step,
    method,
    planKey,
    isSubscription,
    paypalInitRequested,
    paypalTx.orderId,
    isPayPalConfigured,
  ]);

  /* ============================================================
     Stripe handlers
     ============================================================ */

  const handleStripeOneTime = async () => {
    const { data } = await axiosInstance.post("/site/payment/stripe", {
      paymentMethod: "STRIPE",
      creditPlan: planKey,
    });
    const txId = data?.transactionId;
    const clientSecret = data?.clientSecret;
    if (!txId || !clientSecret) {
      setMessage("Payment init failed. Try again.");
      return;
    }
    const cardEl = elements.getElement(CardElement);
    if (!cardEl) {
      setMessage("Card input not ready. Refresh and try again.");
      return;
    }
    const result = await stripe.confirmCardPayment(clientSecret, {
      payment_method: { card: cardEl },
    });
    if (result?.error) {
      setMessage(result.error.message || "Stripe payment failed.");
      return;
    }
    if (result?.paymentIntent?.status !== "succeeded") {
      setMessage("Payment did not complete. Try again.");
      return;
    }
    const notifyRes = await axiosInstance.post("/site/payment/stripe/notification", {
      transactionId: txId,
      // status is intentionally omitted — the backend determines the final
      // payment status by verifying with Stripe directly (webhook or server-side
      // retrieval). Allowing the client to set "PAID_OUT" would let a bad actor
      // grant themselves credits without ever paying.
    });
    if (notifyRes.status === 200 && notifyRes.data?.new_credit !== undefined) {
      if (typeof loadNewCredits === "function") loadNewCredits(notifyRes.data.new_credit);
    }
    setMessage("Payment successful!");
    setSuccess(true);
  };

  const handleStripeSubscription = async () => {
    const { data } = await axiosInstance.post("/site/subscription/stripe", {
      paymentMethod: "STRIPE",
      plan: planKey,
    });
    const subscriptionId = data?.subscriptionId;
    const clientSecret = data?.clientSecret;
    if (!subscriptionId || !clientSecret) {
      setMessage("Subscription init failed. Try again.");
      return;
    }
    const cardEl = elements.getElement(CardElement);
    if (!cardEl) {
      setMessage("Card input not ready. Refresh and try again.");
      return;
    }
    const result = await stripe.confirmCardPayment(clientSecret, {
      payment_method: { card: cardEl },
    });
    if (result?.error) {
      setMessage(result.error.message || "Stripe subscription failed.");
      return;
    }
    if (result?.paymentIntent?.status !== "succeeded") {
      setMessage("Payment did not complete. Try again.");
      return;
    }
    const notifyRes = await axiosInstance.post("/site/subscription/stripe/notification", {
      subscriptionId,
      // status omitted — backend verifies with Stripe.
    });
    if (notifyRes.status === 200 && notifyRes.data?.new_credit !== undefined) {
      if (typeof loadNewCredits === "function") loadNewCredits(notifyRes.data.new_credit);
    }
    setMessage("Monthly subscription activated!");
    setSuccess(true);
  };

  const handleStripeSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) {
      setMessage("Stripe is still loading. Try again.");
      return;
    }
    if (!planKey) {
      setMessage("Please select a plan first.");
      return;
    }
    setLoading(true);
    setMessage("");
    try {
      if (isSubscription) await handleStripeSubscription();
      else await handleStripeOneTime();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) setMessage("Your session expired. Please log in again.");
      else if (status === 400 || status === 409)
        setMessage(err?.response?.data?.message || err?.response?.data?.error || "Payment failed.");
      else if (status >= 500) setMessage("Payment service is temporarily unavailable.");
      else setMessage("Payment failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  /* ============================================================
     PayPal handlers
     ============================================================ */

  const handlePaypalApproveOneTime = async (data, actions) => {
    try {
      setMessage("");
      setLoading(true);
      if (actions?.order?.capture) await actions.order.capture();

      const orderId = paypalTx.orderId || data?.orderID;
      const txId = paypalTx.transactionId;
      if (!orderId || !txId) {
        setMessage("PayPal approval missing order details. Try again.");
        return;
      }
      const notifyRes = await axiosInstance.post(
        `/site/payment/paypal/notification/${encodeURIComponent(orderId)}`,
        { transactionId: txId }
        // status omitted — backend verifies capture with PayPal.
      );
      if (notifyRes.status === 200 && notifyRes.data?.new_credit !== undefined) {
        if (typeof loadNewCredits === "function") loadNewCredits(notifyRes.data.new_credit);
      }
      setMessage("PayPal payment successful!");
      setSuccess(true);
    } catch (err) {
      const status = err?.response?.status;
      setMessage(
        status === 401 || status === 403
          ? "Your session expired. Please log in again."
          : "PayPal payment failed."
      );
    } finally {
      setLoading(false);
    }
  };

  const startPaypalSubscription = async (actions) => {
    try {
      setMessage("");
      setLoading(true);
      const res = await axiosInstance.post("/site/subscription/paypal", {
        paymentMethod: "PAYPAL",
        plan: planKey,
      });
      const subscriptionId = res?.data?.subscriptionId;
      const providerPlanId = res?.data?.providerPlanId;
      if (!subscriptionId || !providerPlanId)
        throw new Error("Could not start PayPal subscription.");
      paypalSubRef.current = { subscriptionId, providerPlanId };
      return actions.subscription.create({ plan_id: providerPlanId });
    } catch (err) {
      const status = err?.response?.status;
      setMessage(
        status === 401 || status === 403
          ? "Your session expired. Please log in again."
          : err?.response?.data?.message || "PayPal subscription failed to start."
      );
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const onPaypalSubscriptionApprove = async (data) => {
    try {
      setLoading(true);
      const subscriptionId = paypalSubRef.current.subscriptionId;
      // PayPal hands us its own subscription ID on the approval payload
      // (data.subscriptionID — note the casing). The backend needs both
      // ours and theirs to reconcile.
      const providerSubscriptionId = data?.subscriptionID;
      if (!subscriptionId || !providerSubscriptionId) {
        setMessage("PayPal subscription approval missing data.");
        return;
      }
      const notifyRes = await axiosInstance.post("/site/subscription/paypal/activate", {
        subscriptionId,
        providerSubscriptionId,
        // status omitted — backend verifies subscription status with PayPal.
      });
      if (notifyRes.status === 200 && notifyRes.data?.new_credit !== undefined) {
        if (typeof loadNewCredits === "function") loadNewCredits(notifyRes.data.new_credit);
      }
      setMessage("PayPal subscription activated!");
      setSuccess(true);
    } catch {
      setMessage("Subscription confirmation failed.");
    } finally {
      setLoading(false);
    }
  };

  /* ============================================================
     Render
     ============================================================ */
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center px-3 sm:px-4 py-4 sm:py-8 overflow-y-auto"
      style={{
        background:
          theme === "dark"
            ? "rgba(0,0,0,0.65)"
            : "rgba(35,54,76,0.55)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
      }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-[480px]"
        onClick={(e) => e.stopPropagation()}
      >
        <Card className="relative">

          {/* Close */}
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center transition-colors"
            style={{
              background: "var(--surf-2)",
              color: "var(--txt-2)",
              border: "1px solid var(--border-2)",
            }}
            aria-label="Close"
          >
            <Icon name="x" size={14} />
          </button>

          {/* Success state */}
          {success ? (
            <div className="text-center py-6">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{
                  background: "var(--teal-dim)",
                  border: "1px solid var(--teal-border)",
                  color: "var(--teal)",
                  animation: "checkPop 0.5s cubic-bezier(.175,.885,.32,1.275) forwards",
                }}
              >
                <Icon name="check" size={28} />
              </div>
              <h2
                className="font-display font-semibold mb-2"
                style={{ fontSize: 22, color: "var(--txt)" }}
              >
                Payment received.
              </h2>
              <p className="text-[13px]" style={{ color: "var(--txt-2)" }}>
                {message || "Your credits will appear shortly."}
              </p>
            </div>
          ) : step === 1 ? (
            /* ===== Step 1 — plan picker ===== */
            <>
              <h2
                className="font-display font-semibold mb-1.5"
                style={{ fontSize: 22, color: "var(--txt)" }}
              >
                Choose a plan.
              </h2>
              <p className="text-[12px] mb-4" style={{ color: "var(--txt-2)" }}>
                One credit = 5 games. Switch plans anytime.
              </p>

              <div className="space-y-2 mb-4">
                {plans.map((p) => (
                  <button
                    key={p.plan}
                    type="button"
                    onClick={() => setPlanKey(p.plan)}
                    className="w-full text-left p-3 rounded-[12px] flex items-center gap-3 transition-colors"
                    style={{
                      background:
                        planKey === p.plan ? "var(--gold-dim)" : "var(--surf-2)",
                      border: `1px solid ${
                        planKey === p.plan ? "var(--gold-border)" : "var(--border)"
                      }`,
                    }}
                  >
                    <div
                      className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{
                        border: `2px solid ${
                          planKey === p.plan ? "var(--gold-lt)" : "var(--txt-3)"
                        }`,
                        background: planKey === p.plan ? "var(--gold-lt)" : "transparent",
                      }}
                    >
                      {planKey === p.plan && (
                        <span
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            background: "#1a0e00",
                          }}
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div
                        className="text-[13px] font-medium"
                        style={{
                          color: planKey === p.plan ? "var(--gold-lt)" : "var(--txt)",
                        }}
                      >
                        {p.title}
                      </div>
                      <div className="text-[11px]" style={{ color: "var(--txt-2)" }}>
                        {p.tagline}
                      </div>
                    </div>
                    <div
                      className="font-display font-semibold flex-shrink-0"
                      style={{
                        fontSize: 16,
                        color: planKey === p.plan ? "var(--gold-lt)" : "var(--txt)",
                      }}
                    >
                      {p.priceLabel}
                    </div>
                  </button>
                ))}
              </div>

              <Button
                full
                disabled={!planKey}
                onClick={() => setStep(2)}
                icon="arrow-right"
                iconRight
              >
                Continue to payment
              </Button>
            </>
          ) : (
            /* ===== Step 2 — checkout ===== */
            <>
              <button
                type="button"
                onClick={() => setStep(1)}
                className="flex items-center gap-1.5 text-[12px] mb-3 hover:underline"
                style={{ color: "var(--txt-2)" }}
              >
                <Icon name="arrow-left" size={13} /> Change plan
              </button>

              <h2
                className="font-display font-semibold mb-1.5"
                style={{ fontSize: 22, color: "var(--txt)" }}
              >
                Checkout.
              </h2>

              {/* Order summary */}
              {selectedPlan && (
                <Card variant="soft" className="!p-3.5 mb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[13px] font-medium" style={{ color: "var(--txt)" }}>
                        {selectedPlan.title}
                      </div>
                      <div className="text-[11px]" style={{ color: "var(--txt-2)" }}>
                        {selectedPlan.tagline}
                      </div>
                    </div>
                    <div
                      className="font-display font-semibold"
                      style={{ fontSize: 20, color: "var(--gold-lt)" }}
                    >
                      {formattedAmount}
                      {isSubscription && (
                        <span
                          className="text-[12px] font-normal"
                          style={{ color: "var(--txt-2)" }}
                        >
                          /mo
                        </span>
                      )}
                    </div>
                  </div>
                </Card>
              )}

              {/* Method toggle */}
              <div className="flex gap-2 mb-4">
                <MethodTab
                  selected={method === "card"}
                  onClick={() => setMethod("card")}
                  icon="credit-card"
                  label="Card"
                />
                {isPayPalConfigured && (
                  <MethodTab
                    selected={method === "paypal"}
                    onClick={() => setMethod("paypal")}
                    icon="brand-paypal"
                    label="PayPal"
                  />
                )}
              </div>

              {/* Error */}
              {message && !success && (
                <div
                  className="mb-4 p-3 rounded-[10px] text-[12px] flex items-start gap-2"
                  style={{
                    background: "var(--red-dim)",
                    border: "1px solid var(--red-border)",
                    color: "var(--red)",
                  }}
                >
                  <Icon name="alert-circle" size={14} style={{ marginTop: 1 }} />
                  <span>{message}</span>
                </div>
              )}

              {/* Stripe form */}
              {method === "card" && (
                <form onSubmit={handleStripeSubmit}>
                  <div
                    className="p-3.5 rounded-[10px] mb-3"
                    style={{
                      background: "var(--surf-2)",
                      border: "1px solid var(--border-2)",
                    }}
                  >
                    <CardElement
                      // Stripe's CardElement runs in a cross-origin iframe
                      // and can't read CSS variables — so we hand it
                      // theme-resolved hex values directly. Keyed by `theme`
                      // so it re-renders with the right colors on toggle.
                      key={theme}
                      options={{
                        hidePostalCode: false,
                        style: {
                          base: {
                            color: theme === "dark" ? "#FFFFFF" : "#23364C",
                            fontFamily: "'Inter', sans-serif",
                            fontSize: "14px",
                            "::placeholder": {
                              color: theme === "dark"
                                ? "rgba(255,255,255,0.48)"
                                : "#8D8B9E",
                            },
                          },
                          invalid: {
                            color: theme === "dark" ? "#FF6B62" : "#D6453C",
                          },
                        },
                      }}
                    />
                  </div>
                  <div
                    className="flex items-center gap-1.5 mb-4 text-[11px]"
                    style={{ color: "var(--txt-3)" }}
                  >
                    <Icon name="shield-check" size={12} />
                    Secured by Stripe · We never store card data
                  </div>
                  <Button type="submit" full loading={loading} icon="lock">
                    {loading
                      ? "Processing..."
                      : isSubscription
                      ? `Subscribe for ${formattedAmount}/mo`
                      : `Pay ${formattedAmount}`}
                  </Button>
                </form>
              )}

              {/* PayPal */}
              {method === "paypal" && isPayPalConfigured && (
                <PayPalScriptProvider
                  options={{
                    "client-id": PAYPAL_CLIENT_ID,
                    currency: PAYPAL_CURRENCY,
                    intent: isSubscription ? "subscription" : "capture",
                    vault: isSubscription,
                  }}
                >
                  <div className="relative">
                    {isSubscription ? (
                      <PayPalButtons
                        style={{ layout: "vertical", shape: "rect", color: "gold" }}
                        createSubscription={(_data, actions) => startPaypalSubscription(actions)}
                        onApprove={onPaypalSubscriptionApprove}
                        onError={() => setMessage("PayPal subscription error.")}
                      />
                    ) : (
                      paypalTx.orderId && (
                        <PayPalButtons
                          style={{ layout: "vertical", shape: "rect", color: "gold" }}
                          createOrder={() => paypalTx.orderId}
                          onApprove={handlePaypalApproveOneTime}
                          onError={() => setMessage("PayPal payment error.")}
                        />
                      )
                    )}
                    {loading && (
                      <div
                        className="absolute inset-0 flex items-center justify-center rounded-[10px]"
                        style={{
                          background:
                            theme === "dark"
                              ? "rgba(0,0,0,0.55)"
                              : "rgba(35,54,76,0.45)",
                        }}
                      >
                        <Icon
                          name="loader-2"
                          size={22}
                          style={{ color: "var(--gold-lt)" }}
                        />
                      </div>
                    )}
                  </div>
                </PayPalScriptProvider>
              )}

              <p className="text-[10px] text-center mt-4" style={{ color: "var(--txt-3)" }}>
                Entertainment only · 18+ · Eligible US states · Refund policy applies
              </p>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}

function MethodTab({ selected, onClick, icon, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex-1 py-2.5 rounded-[10px] text-[12px] font-medium flex items-center justify-center gap-2 transition-colors"
      style={{
        background: selected ? "var(--gold-dim)" : "var(--surf-2)",
        color: selected ? "var(--gold-lt)" : "var(--txt-2)",
        border: `1px solid ${selected ? "var(--gold-border)" : "var(--border)"}`,
      }}
    >
      <Icon name={icon} size={14} />
      {label}
    </button>
  );
}
